import { Path } from "../../../lib/runescape/pathing";
import { Vector2 } from "../../../lib/math";
import { tap } from "lodash";
import { Clues } from "../../model/Clues";
import { util } from "../../../lib/util/util";
import { TileCoordinates } from "../../../lib/runescape/coordinates";
import { PathingGraphics } from "../../ui/path_graphics";
import { TileArea } from "../../../lib/runescape/coordinates/TileArea";
import { Scans } from "../Scans";
import { FakeLodash } from "../../../lib/coreutil/FakeLodash";
/**
 * Scan Trees are decision trees used to solve scan clues efficiently.
 */
export var ScanTree;
(function (ScanTree) {
    var movement_state = Path.movement_state;
    var digSpotArea = Clues.digSpotArea;
    var PulseInformation = Scans.PulseInformation;
    var spot_narrowing = Scans.spot_narrowing;
    let Augmentation;
    (function (Augmentation) {
        var avg = util.avg;
        var ends_up = Path.ends_up;
        let Timing;
        (function (Timing) {
            let Statistics;
            (function (Statistics) {
                var median = util.median;
                function calculate(ticks) {
                    ticks.sort();
                    return {
                        average: avg(...ticks),
                        min: ticks[0],
                        max: ticks[ticks.length - 1],
                        median: median(ticks),
                    };
                }
                Statistics.calculate = calculate;
            })(Statistics = Timing.Statistics || (Timing.Statistics = {}));
            function from(...ticks) {
                const combined = {
                    spots: [],
                    statistics: undefined
                };
                ticks.forEach(({ spot, ticks, incomplete }) => {
                    const entry = combined.spots.find(s => TileCoordinates.eq(s.spot, spot))
                        ?? tap({ spot: spot, timings: [], statistics: undefined }, e => combined.spots.push(e));
                    entry.timings.push({ ticks, incomplete });
                });
                combined.spots.forEach(e => e.statistics = Timing.Statistics.calculate(e.timings.map(t => t.ticks)));
                combined.statistics = Timing.Statistics.calculate(combined.spots.flatMap(e => e.timings.map(t => t.ticks)));
                return combined;
            }
            Timing.from = from;
            function flat(self) {
                return self.spots.flatMap(s => s.timings.map(t => ({ spot: s.spot, ticks: t.ticks, incomplete: t.incomplete })));
            }
            Timing.flat = flat;
            function combine(...a) {
                return from(...a.flatMap(s => flat(s)));
            }
            Timing.combine = combine;
        })(Timing = Augmentation.Timing || (Augmentation.Timing = {}));
        let AugmentedScanTreeNode;
        (function (AugmentedScanTreeNode) {
            function uncycled(node) {
                const copy = { ...node };
                copy.parent = undefined;
                copy.root = undefined;
                copy.children.forEach(c => c.value = uncycled(c.value));
                return copy;
            }
            AugmentedScanTreeNode.uncycled = uncycled;
        })(AugmentedScanTreeNode = Augmentation.AugmentedScanTreeNode || (Augmentation.AugmentedScanTreeNode = {}));
        let NodeId;
        (function (NodeId) {
            function of(node) {
                const id = new Array(node.depth);
                let i = node.depth - 1;
                while (node.parent) {
                    id[i] = node.parent.key;
                    node = node.parent.node;
                    i--;
                }
                return id;
            }
            NodeId.of = of;
            function hash(id) {
                return id.map(PulseInformation.toString).join("");
            }
            NodeId.hash = hash;
        })(NodeId = Augmentation.NodeId || (Augmentation.NodeId = {}));
        let Correctness;
        (function (Correctness) {
            function meta(completeness) {
                const meta = {
                    complete: { char: "\u2713", cls: "ctr-correct", desc: "This branch is complete." },
                    correct: { char: "\u2713", cls: "ctr-correct", desc: "All paths are correct." },
                    correct_with_warnings: { char: "\u2713", cls: "ctr-semicorrect", desc: "All paths are correct, but some have warnings." },
                    error: { char: "!", cls: "ctr-incorrect", desc: "There is an error in this path." },
                    error_in_children: { char: "!", cls: "ctr-semiincorrect", desc: "A child path has errors." },
                    incomplete: { char: "?", cls: "ctr-incorrect", desc: "This branch is incomplete." },
                    incomplete_children: { char: "?", cls: "ctr-semiincorrect", desc: "Branch has incomplete children." }
                };
                return meta[completeness];
            }
            Correctness.meta = meta;
        })(Correctness = Augmentation.Correctness || (Augmentation.Correctness = {}));
        /**
         * Augments all paths in an (already augmented!) decision tree.
         * Modifies the input tree.
         *
         * @param tree The tree whose paths to augment
         * @param assumptions The underlying path assumptions
         */
        async function path_augmentation(tree, assumptions) {
            async function helper(node, start_state) {
                node.path = await Path.augment(node.raw.path, start_state, node.remaining_candidates.length == 1
                    ? [TileArea.activate(digSpotArea(node.remaining_candidates[0]))]
                    : node.region?.area ? [TileArea.activate(node.region.area)] : []);
                if (node.children.length > 0) {
                    let cloned_state = FakeLodash.cloneDeep(node.path.post_state);
                    cloned_state.tick += 1; // Assume 1 tick reaction time between steps. Approximation, but may help to make timings and cooldowns more realistic
                    await Promise.all(node.children.map(c => helper(c.value, cloned_state)));
                }
            }
            if (!tree.state.paths_augmented) {
                await helper(tree.root_node, movement_state.start(assumptions));
                tree.state.paths_augmented = true;
            }
            return tree;
        }
        Augmentation.path_augmentation = path_augmentation;
        /**
         * Basic augmentation of a ScanTree. Translates a basic scan tree into a richer data structure that is easier to work with.
         * - Propagates the immediate parents down the tree and saves them.
         * - Sets the depth for each node
         * - Sets the remaining candidates on each node
         *
         * @param tree
         * @param clue
         */
        function basic_augmentation(tree, clue) {
            let root = {
                raw: tree,
                clue: clue,
                root_node: null,
                state: {
                    paths_augmented: false,
                    completeness_analyzed: false,
                    correctness_analyzed: false,
                    timing_analysis: null,
                    has_synthetic_triple_nodes: false
                }
            };
            /**
             * Recursive helper function passing along context information.
             * @param node The traversed node.
             * @param parent The parent node.
             * @param depth The depth of the node.
             * @param remaining_candidates Target spots that are still possible at this point in the scan tree.
             * @param last_known_position The position where the player is before entering this node, if known.
             */
            function helper(node, parent, depth, remaining_candidates, last_known_position) {
                let t = {
                    root: root,
                    parent: parent,
                    region: {
                        area: node.region?.area || Path.endsUpArea(node.path) || last_known_position,
                        name: node.region?.name ?? ""
                    },
                    raw: node,
                    depth: depth,
                    remaining_candidates: remaining_candidates,
                    children: []
                };
                if (node.children.length > 0) {
                    let narrowing = spot_narrowing(remaining_candidates, t.region.area, tree.assumed_range);
                    // The node is not a leaf node, handle all relevant children
                    t.children = node.children.map(child => {
                        return {
                            key: child.key,
                            value: helper(child ? child.value : null, { node: t, key: child.key }, depth + 1, narrowing.find(n => PulseInformation.equals(n.pulse, child.key)).narrowed_candidates, t.region.area)
                        };
                    });
                }
                return t;
            }
            root.root_node = helper(tree.root, null, 0, tree.ordered_spots, null);
            return root;
        }
        Augmentation.basic_augmentation = basic_augmentation;
        /**
         * Analyzes an already augmented decision tree for correctness and completeness.
         * Results are written into the AugmentedScanTreeNode
         *
         * @param tree
         */
        function analyze_correctness(tree) {
            function helper(node) {
                node.children.forEach(c => helper(c.value));
                let cs = node.children.map(c => c.value);
                let issues = Path.collect_issues(node.path);
                if (issues.some(i => i.level == "error"))
                    node.correctness = "error";
                else if (cs.some(c => c.correctness == "error" || c.correctness == "error_in_children"))
                    node.correctness = "error_in_children";
                else if (issues.some(i => i.level == "warning") || cs.some(c => c.correctness == "correct_with_warnings"))
                    node.correctness = "correct_with_warnings";
                else
                    node.correctness = "correct";
            }
            if (!tree.state.paths_augmented)
                throw new TypeError("Trying to analyze a decision tree for correctness without augmented paths!");
            if (!tree.state.correctness_analyzed) {
                helper(tree.root_node);
                tree.state.correctness_analyzed = true;
            }
            return tree;
        }
        Augmentation.analyze_correctness = analyze_correctness;
        /**
         * Analyzes an already augmented decision tree for its completeness and annotates every node with it
         *
         * @param tree
         */
        function analyze_completeness(tree) {
            function helper(node) {
                node.children.forEach(c => helper(c.value));
                let cs = node.children.map(c => c.value);
                node.completeness = "complete";
                if (node.remaining_candidates.length > 1 && cs.length == 0)
                    node.completeness = "incomplete";
                else if (cs.some(c => c.completeness == "incomplete" || c.completeness == "incomplete_children"))
                    node.completeness = "incomplete_children";
                else if (node.remaining_candidates.length == 1) {
                    const e = ends_up(node.raw.path);
                    if (!e || !TileArea.activate(digSpotArea(node.remaining_candidates[0])).query(e)) {
                        node.completeness = "incomplete";
                    }
                }
            }
            if (!tree.state.completeness_analyzed) {
                helper(tree.root_node);
                tree.state.completeness_analyzed = true;
            }
            return tree;
        }
        Augmentation.analyze_completeness = analyze_completeness;
        function analyze_timing(tree) {
            AugmentedScanTree.traversePostOrder(tree.root_node, (node) => {
                if (node.children.length == 0) {
                    node.timing_analysis = Timing.from(...node.remaining_candidates.map(c => ({ spot: c, ticks: node.path.post_state.tick, incomplete: node.completeness != "complete" })));
                }
                else {
                    node.timing_analysis = Timing.combine(...node.children.map(c => c.value.timing_analysis));
                }
            });
            tree.state.timing_analysis = tree.root_node.timing_analysis;
            return tree;
        }
        Augmentation.analyze_timing = analyze_timing;
        function synthesize_triple_nodes(tree) {
            if (tree.state.has_synthetic_triple_nodes)
                return tree;
            AugmentedScanTree.traverse(tree.root_node, n => {
                const triples = n.children.filter(c => c.key.pulse == 3);
                if (triples.length > 1 && n.children.some(c => c.key.pulse != 3)) {
                    const synthetic = {
                        children: [],
                        depth: n.depth + 1,
                        parent: { key: { pulse: 3 }, node: n },
                        path: n.path ? {
                            issues: [], post_state: n.path.post_state, pre_state: n.path.post_state, raw: [], steps: [], target: []
                        } : undefined,
                        raw: {
                            children: [], directions: undefined, path: [], region: undefined
                        },
                        region: n.region,
                        remaining_candidates: triples.flatMap(t => t.value.remaining_candidates),
                        root: n.root
                    };
                    synthetic.children = n.children.filter(c => c.key.pulse == 3).map((c) => ({
                        key: c.key,
                        value: {
                            ...c.value,
                            depth: c.value.depth + 1,
                            parent: { node: synthetic, key: c.key }
                        }
                    }));
                    n.children = [...n.children.filter(c => c.key.pulse != 3), {
                            key: { pulse: 3 },
                            value: synthetic
                        }];
                }
            });
            return tree;
        }
        Augmentation.synthesize_triple_nodes = synthesize_triple_nodes;
        async function augment(options, tree, clue) {
            const augmented = basic_augmentation(tree, clue);
            if (options.augment_paths)
                await path_augmentation(augmented, options.path_assumptions);
            if (options.analyze_correctness)
                analyze_correctness(augmented);
            if (options.analyze_completeness)
                analyze_completeness(augmented);
            if (options.analyze_timing)
                analyze_timing(augmented);
            if (options.synthesize_triple_nodes)
                synthesize_triple_nodes(augmented);
            return augmented;
        }
        Augmentation.augment = augment;
        let AugmentedScanTree;
        (function (AugmentedScanTree) {
            function decision_string(node) {
                if (node.parent?.node?.parent?.key?.pulse == 3 && node.remaining_candidates.length == 1)
                    return `Sp. ${spotNumber(node.root.raw, node.remaining_candidates[0])}`;
                const internal = (() => {
                    if (!node.parent)
                        return "Start";
                    else {
                        const region_name = node.parent.node.region?.name || "";
                        const type = node.parent.key;
                        const context = node.parent.node.children.map(c => c.key);
                        // Use the full word when it's not "different level"
                        if (!type.different_level) {
                            if (util.count(context, (p => p.different_level)) == context.length - 1) {
                                // Is the only non-different level
                                if (region_name.length > 0)
                                    return `${region_name},Far`;
                                else
                                    return "Far";
                            }
                            else
                                return `${region_name}${type.pulse.toString()}`;
                        }
                        else {
                            const counterpart_exists = context.some(p => p.pulse == type.pulse && !p.different_level);
                            if (!counterpart_exists)
                                return type.pulse.toString(); // If the non-different level counterpart does not exist, just use the pretty string
                            if (util.count(context, (p => p.different_level)) == 1) {
                                // Is the only different level
                                if (region_name.length > 0)
                                    return `${region_name},DL`;
                                else
                                    return "DL";
                            }
                            else
                                return `${region_name}${type.pulse}DL`;
                        }
                    }
                })();
                if (node.remaining_candidates.length == 1)
                    return `${internal}: Sp. ${spotNumber(node.root.raw, node.remaining_candidates[0])}`;
                else
                    return internal;
            }
            AugmentedScanTree.decision_string = decision_string;
            function collect_parents(node, include_node = true) {
                if (!node)
                    return [];
                let par = collect_parents(node.parent?.node, true);
                if (include_node)
                    par.push(node);
                return par;
            }
            AugmentedScanTree.collect_parents = collect_parents;
            function collect_children(node, include_node = true) {
                const acc = [];
                traverse(node, n => acc.push(n), include_node);
                return acc;
            }
            AugmentedScanTree.collect_children = collect_children;
            function collect_parents_and_children(node) {
                const nodes = collect_parents(node, false);
                traverse(node, n => nodes.push(n), false);
                return nodes;
            }
            AugmentedScanTree.collect_parents_and_children = collect_parents_and_children;
            /**
             * Pre-Order Traversal
             */
            function traverse(tree, f, include_root = true) {
                if (include_root && tree)
                    f(tree);
                tree.children.forEach(c => traverse(c.value, f, true));
            }
            AugmentedScanTree.traverse = traverse;
            /**
             * Post-Order Traversal
             */
            function traversePostOrder(tree, f, include_root = true) {
                tree.children.forEach(c => traversePostOrder(c.value, f, true));
                if (include_root && tree)
                    f(tree);
            }
            AugmentedScanTree.traversePostOrder = traversePostOrder;
        })(AugmentedScanTree = Augmentation.AugmentedScanTree || (Augmentation.AugmentedScanTree = {}));
    })(Augmentation = ScanTree.Augmentation || (ScanTree.Augmentation = {}));
    /**
     * Traverse a given scan tree in a pre-order fashion.
     * @param tree The tree to traverse.
     * @param f The function to apply to each node.
     */
    function traverse(tree, f) {
        if (tree)
            f(tree);
        tree.children.forEach(c => traverse(c.value, f));
    }
    ScanTree.traverse = traverse;
    /**
     * Initialize an empty scan tree for the given scan clue.
     * @param clue The clue the new scan tree is supposed to solve.
     */
    function init(clue) {
        return {
            assumed_range: clue.range,
            ordered_spots: clue.spots,
            root: init_leaf()
        };
    }
    ScanTree.init = init;
    /**
     * Initialize a new leaf node.
     */
    function init_leaf() {
        return {
            children: [],
            directions: "",
            path: [],
        };
    }
    ScanTree.init_leaf = init_leaf;
    /**
     * Scan Tree normalization prunes and sanitizes a given scan tree.
     * This is used in the scan editor to ensure scan trees remain valid.
     * @param tree
     */
    function normalize(tree) {
        /**
         * Recursive helper function to traverse the tree
         * @param node The node to traverse.
         * @param candidates The set of candidate spots that are still possible at this point in the tree
         */
        function helper(node, candidates) {
            // Find out where the player is at this point in the tree
            const where = node.region?.area || Path.endsUpArea(node.path);
            // Update children to remove all dead branches and add missing branches
            let pruned_children = !where ? []
                : spot_narrowing(candidates, where, tree.assumed_range) // Split the remaining candidates by the possible pulses at the current location
                    .filter(n => n.narrowed_candidates.length > 0) // Delete branches that have no candidates left
                    .map(({ pulse, narrowed_candidates }) => {
                    // For each split that has candidates, create a child node. Reuse existing ones if they exist.
                    return {
                        child: node.children.find(c => PulseInformation.equals(pulse, c.key)) || {
                            key: pulse,
                            value: init_leaf()
                        },
                        candidates: narrowed_candidates
                    };
                });
            // When there is only one child, the current position produces no information at all
            // So there is no point in adding children, which is why they are removed by this statement
            if (pruned_children.length == 1)
                pruned_children = [];
            // Update the children in the node by the newly pruned children
            node.children = pruned_children.map(c => c.child);
            // Continue traversal
            pruned_children.forEach(({ child, candidates }) => {
                // Propagate state recursively
                helper(child.value, candidates);
            });
        }
        if (!tree.root)
            tree.root = init_leaf();
        helper(tree.root, tree.ordered_spots);
        return tree;
    }
    ScanTree.normalize = normalize;
    /**
     * Gets the spot number for the given target spot as defined in the scan tree.
     * @param self The reference scan tree.
     * @param spot The target spot.
     */
    function spotNumber(self, spot) {
        return self.ordered_spots.findIndex((s) => Vector2.eq(s, spot)) + 1;
    }
    ScanTree.spotNumber = spotNumber;
    function defaultScanTreeInstructions(node) {
        if (node.raw.path.length == 0 && node.children.length == 0)
            return "Improvise!";
        const path_short = node.raw.path.length > 0
            ? node.raw.path.filter(p => p.type != "cosmetic").map(PathingGraphics.templateString).join(" - ")
            : "Go";
        /* TODO:
            - Run followed by run is combined
            - Runs <= 4 tiles are ignored if they are not the only step
            - Abilities after a powerburst are compressed
         */
        const target = node.raw.path.length == 0 || node.raw.region?.name || node.remaining_candidates.length == 1
            ? " to {{target}}" : "";
        return path_short + target;
    }
    ScanTree.defaultScanTreeInstructions = defaultScanTreeInstructions;
    function getInstruction(node) {
        return node.raw.directions || defaultScanTreeInstructions(node);
    }
    ScanTree.getInstruction = getInstruction;
    function getTargetRegion(node) {
        if (node.remaining_candidates.length == 1)
            return null;
        return { area: node.region.area, name: node.region?.name ?? "" };
    }
    ScanTree.getTargetRegion = getTargetRegion;
})(ScanTree || (ScanTree = {}));
